import { useState, useCallback } from 'react';
import type { UseAdminAppReturn } from './useAdminApp';
import { messageStructureSchemas } from '@pineseed/spec';
import type { z } from 'zod';

// Extract types from schemas
type MessageReaction = z.infer<typeof messageStructureSchemas.MessageReaction>;
type MessageReadStatus = z.infer<typeof messageStructureSchemas.MessageRead>;
type MessageReply = z.infer<typeof messageStructureSchemas.MessageReply>;

interface MarkAsReadRequest {
  participantId: string;
  messageId: string;
  threadId: string;
}

interface AddReplyRequest {
  replyMessageId: string;
  originalMessageId: string;
  participantId: string;
  threadId: string;
}

interface AddReactionRequest {
  participantId: string;
  messageId: string;
  threadId: string;
  emoji: string;
}

interface ReactionSummary {
  [emoji: string]: number;
}

export interface MessengerAdminHandler {
  // State
  reactions: MessageReaction[];
  readStatuses: MessageReadStatus[];
  replies: MessageReply[];
  reactionSummary: Record<string, ReactionSummary>;
  loading: boolean;
  error: string | null;
  
  // Read operations
  markMessageAsRead: (request: MarkAsReadRequest) => Promise<MessageReadStatus>;
  getMessageReadStatus: (participantId: string, messageId: string) => Promise<MessageReadStatus | null>;
  
  // Reply operations
  addReply: (request: AddReplyRequest) => Promise<MessageReply>;
  getMessageReplies: (messageId: string) => Promise<MessageReply[]>;
  
  // Reaction operations
  addReaction: (request: AddReactionRequest) => Promise<MessageReaction>;
  removeReaction: (participantId: string, messageId: string) => Promise<void>;
  getMessageReactions: (messageId: string) => Promise<MessageReaction[]>;
  getReactionSummary: (messageId: string) => Promise<ReactionSummary>;
  
  // Clear state
  clearState: () => void;
}

export function useMessengerAdmin(adminApp: UseAdminAppReturn): MessengerAdminHandler {
  const [reactions, setReactions] = useState<MessageReaction[]>([]);
  const [readStatuses, setReadStatuses] = useState<MessageReadStatus[]>([]);
  const [replies, setReplies] = useState<MessageReply[]>([]);
  const [reactionSummary, setReactionSummary] = useState<Record<string, ReactionSummary>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mark message as read
  const markMessageAsRead = useCallback(async (request: MarkAsReadRequest): Promise<MessageReadStatus> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/read`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to mark message as read: ${response.statusText}`);
      }

      const data = await response.json() as MessageReadStatus;
      console.log('✅ [MessengerAdmin] Marked message as read:', data);
      
      // Update local state
      setReadStatuses(prev => {
        const existing = prev.findIndex(r => r.participantId === data.participantId && r.messageId === data.messageId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = data;
          return updated;
        }
        return [...prev, data];
      });
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark message as read';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Mark as read error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Get message read status
  const getMessageReadStatus = useCallback(async (participantId: string, messageId: string): Promise<MessageReadStatus | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/read?participantId=${encodeURIComponent(participantId)}&messageId=${encodeURIComponent(messageId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get read status: ${response.statusText}`);
      }

      const data = await response.json() as MessageReadStatus;
      console.log('✅ [MessengerAdmin] Got read status:', data);
      
      // Add to local state
      setReadStatuses(prev => {
        const existing = prev.findIndex(r => r.participantId === data.participantId && r.messageId === data.messageId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = data;
          return updated;
        }
        return [...prev, data];
      });
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get read status';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Get read status error:', errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Add reply
  const addReply = useCallback(async (request: AddReplyRequest): Promise<MessageReply> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/reply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add reply: ${response.statusText}`);
      }

      const data = await response.json() as MessageReply;
      console.log('✅ [MessengerAdmin] Added reply:', data);
      
      // Update local state
      setReplies(prev => [...prev, data]);
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add reply';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Add reply error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Get message replies
  const getMessageReplies = useCallback(async (messageId: string): Promise<MessageReply[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/replies?messageId=${encodeURIComponent(messageId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get replies: ${response.statusText}`);
      }

      const data = await response.json();
      const messageReplies = data.replies || [];
      console.log('✅ [MessengerAdmin] Got replies:', messageReplies);
      
      // Update local state
      setReplies(messageReplies);
      
      return messageReplies;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get replies';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Get replies error:', errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Add reaction
  const addReaction = useCallback(async (request: AddReactionRequest): Promise<MessageReaction> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/reaction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add reaction: ${response.statusText}`);
      }

      const data = await response.json() as MessageReaction;
      console.log('✅ [MessengerAdmin] Added reaction:', data);
      
      // Update local state
      setReactions(prev => {
        const existing = prev.findIndex(r => r.participantId === data.participantId && r.messageId === data.messageId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = data;
          return updated;
        }
        return [...prev, data];
      });
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add reaction';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Add reaction error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Remove reaction
  const removeReaction = useCallback(async (participantId: string, messageId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/reaction?participantId=${encodeURIComponent(participantId)}&messageId=${encodeURIComponent(messageId)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to remove reaction: ${response.statusText}`);
      }

      console.log('✅ [MessengerAdmin] Removed reaction');
      
      // Update local state
      setReactions(prev => prev.filter(r => !(r.participantId === participantId && r.messageId === messageId)));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove reaction';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Remove reaction error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Get message reactions
  const getMessageReactions = useCallback(async (messageId: string): Promise<MessageReaction[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/reactions?messageId=${encodeURIComponent(messageId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get reactions: ${response.statusText}`);
      }

      const data = await response.json();
      const messageReactions = data.reactions || [];
      console.log('✅ [MessengerAdmin] Got reactions:', messageReactions);
      
      // Update local state for this message
      setReactions(prev => {
        const filtered = prev.filter(r => r.messageId !== messageId);
        return [...filtered, ...messageReactions];
      });
      
      return messageReactions;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get reactions';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Get reactions error:', errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Get reaction summary
  const getReactionSummary = useCallback(async (messageId: string): Promise<ReactionSummary> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/message/reaction-summary?messageId=${encodeURIComponent(messageId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get reaction summary: ${response.statusText}`);
      }

      const data = await response.json();
      const summary = data.summary || {};
      console.log('✅ [MessengerAdmin] Got reaction summary:', summary);
      
      // Update local state
      setReactionSummary(prev => ({ ...prev, [messageId]: summary }));
      
      return summary;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get reaction summary';
      setError(errorMessage);
      console.error('❌ [MessengerAdmin] Get reaction summary error:', errorMessage);
      return {};
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const clearState = useCallback(() => {
    setReactions([]);
    setReadStatuses([]);
    setReplies([]);
    setReactionSummary({});
    setError(null);
  }, []);

  return {
    reactions,
    readStatuses,
    replies,
    reactionSummary,
    loading,
    error,
    markMessageAsRead,
    getMessageReadStatus,
    addReply,
    getMessageReplies,
    addReaction,
    removeReaction,
    getMessageReactions,
    getReactionSummary,
    clearState,
  };
}

